// src/services/ErrorLoggerService.ts
/**
 * Centralized Error Logging Service
 * Logs all application errors to Supabase for admin monitoring
 */

import { supabase } from '../config/supabase';

export type ErrorType = 'network' | 'validation' | 'runtime' | 'auth' | 'import' | 'api' | 'database' | 'unknown';
export type ErrorSeverity = 'critical' | 'error' | 'warning' | 'info';

export interface ErrorLogData {
  errorType: ErrorType;
  errorMessage: string;
  errorStack?: string;
  errorCode?: string;
  componentName?: string;
  actionName?: string;
  pagePath?: string;
  severity?: ErrorSeverity;
  metadata?: Record<string, any>;
}

export interface ErrorLog {
  id: string;
  user_id: string | null;
  user_email: string | null;
  session_id: string | null;
  error_type: string;
  error_message: string;
  error_stack: string | null;
  error_code: string | null;
  component_name: string | null;
  action_name: string | null;
  page_path: string | null;
  severity: string;
  metadata: Record<string, any>;
  is_resolved: boolean;
  created_at: string;
}

export interface ErrorStats {
  total_errors: number;
  critical_errors: number;
  unresolved_errors: number;
  errors_by_type: Record<string, number>;
  errors_by_page: Record<string, number>;
  top_affected_users: Array<{ user_email: string; error_count: number }>;
}

export class ErrorLoggerService {
  private static sessionId: string | null = null;
  private static currentUserId: string | null = null;
  private static currentUserEmail: string | null = null;

  // ── Deduplication: track hash → expiry timestamp ──
  private static recentErrors = new Map<string, number>();
  private static readonly DEDUP_WINDOW_MS = 60_000; // same error suppressed for 60s

  // ── Offline queue: buffer errors when Supabase is unavailable ──
  private static offlineQueue: Array<Record<string, any>> = [];
  private static readonly MAX_QUEUE_SIZE = 100;
  private static isFlushingQueue = false;

  // ── Rate limiting: max errors logged per minute ──
  private static minuteWindowStart = Date.now();
  private static minuteCount = 0;
  private static readonly MAX_PER_MINUTE = 50;

  /** Build a stable fingerprint for an error to use as dedup key */
  private static fingerprint(data: ErrorLogData): string {
    return `${data.errorType}|${data.componentName || ''}|${data.actionName || ''}|${data.errorMessage.slice(0, 120)}`;
  }

  /** Returns true if this error should be suppressed (duplicate or rate-limited) */
  private static shouldSuppress(data: ErrorLogData): boolean {
    // Rate limiting
    const now = Date.now();
    if (now - this.minuteWindowStart > 60_000) {
      this.minuteWindowStart = now;
      this.minuteCount = 0;
    }
    if (this.minuteCount >= this.MAX_PER_MINUTE) {
      return true;
    }

    // Deduplication
    const key = this.fingerprint(data);
    const expiry = this.recentErrors.get(key);
    if (expiry && now < expiry) {
      return true;
    }
    this.recentErrors.set(key, now + this.DEDUP_WINDOW_MS);
    this.minuteCount++;

    // Prune stale dedup entries periodically
    if (this.recentErrors.size > 200) {
      for (const [k, exp] of this.recentErrors) {
        if (now > exp) this.recentErrors.delete(k);
      }
    }

    return false;
  }

  /** Attempt to flush queued errors when connectivity is restored */
  private static async flushOfflineQueue(): Promise<void> {
    if (this.isFlushingQueue || this.offlineQueue.length === 0) return;
    this.isFlushingQueue = true;
    try {
      const batch = this.offlineQueue.splice(0, 20);
      const { error } = await supabase.from('error_logs').insert(batch);
      if (error) {
        // Put them back at the front
        this.offlineQueue.unshift(...batch);
      }
    } catch {
      // ignore flush failures
    } finally {
      this.isFlushingQueue = false;
    }
  }

  /**
   * Initialize error logger with user context
   */
  static init(userId: string, userEmail: string, sessionId?: string): void {
    this.currentUserId = userId;
    this.currentUserEmail = userEmail;
    this.sessionId = sessionId || null;

    // Set up global error handler
    this.setupGlobalErrorHandlers();
  }

  /**
   * Clear user context (on logout)
   */
  static clear(): void {
    this.currentUserId = null;
    this.currentUserEmail = null;
    this.sessionId = null;
  }

  /**
   * Log an error to Supabase with deduplication, rate limiting, and offline queuing.
   */
  static async logError(data: ErrorLogData): Promise<void> {
    // Skip info-level entries from dedup/rate-limit checks
    if (data.severity !== 'info' && this.shouldSuppress(data)) {
      return;
    }

    try {
      const errorLog = {
        user_id: this.currentUserId,
        user_email: this.currentUserEmail,
        session_id: this.sessionId,
        error_type: data.errorType,
        error_message: data.errorMessage,
        error_stack: data.errorStack || null,
        error_code: data.errorCode || null,
        component_name: data.componentName || null,
        action_name: data.actionName || null,
        page_path: data.pagePath || window.location.pathname,
        severity: data.severity || 'error',
        metadata: data.metadata || {},
        user_agent: navigator.userAgent,
        browser_info: this.getBrowserInfo()
      };

      // Attempt to flush any previously queued errors first
      if (this.offlineQueue.length > 0) {
        this.flushOfflineQueue(); // fire-and-forget
      }

      // Log to Supabase
      const { error } = await supabase
        .from('error_logs')
        .insert(errorLog);

      if (error) {
        // Queue for later if Supabase is unavailable
        if (this.offlineQueue.length < this.MAX_QUEUE_SIZE) {
          this.offlineQueue.push(errorLog);
        }
        console.error('[ErrorLogger] Failed to log to Supabase (queued):', error);
      }
    } catch (err) {
      // Prevent infinite loop - just log to console
      console.error('[ErrorLogger] Exception while logging error:', err);
    }
  }

  /**
   * Log a network error
   */
  static async logNetworkError(
    message: string,
    url: string,
    statusCode?: number,
    componentName?: string
  ): Promise<void> {
    await this.logError({
      errorType: 'network',
      errorMessage: message,
      componentName,
      actionName: 'network_request',
      severity: statusCode && statusCode >= 500 ? 'critical' : 'error',
      metadata: { url, statusCode }
    });
  }

  /**
   * Log a validation error
   */
  static async logValidationError(
    message: string,
    field?: string,
    value?: any,
    componentName?: string
  ): Promise<void> {
    await this.logError({
      errorType: 'validation',
      errorMessage: message,
      componentName,
      severity: 'warning',
      metadata: { field, value }
    });
  }

  /**
   * Log a runtime error
   */
  static async logRuntimeError(
    error: Error,
    componentName?: string,
    actionName?: string
  ): Promise<void> {
    await this.logError({
      errorType: 'runtime',
      errorMessage: error.message,
      errorStack: error.stack,
      componentName,
      actionName,
      severity: 'error'
    });
  }

  /**
   * Log an authentication error
   */
  static async logAuthError(
    message: string,
    errorCode?: string,
    componentName?: string
  ): Promise<void> {
    await this.logError({
      errorType: 'auth',
      errorMessage: message,
      errorCode,
      componentName,
      severity: 'error'
    });
  }

  /**
   * Log an import/data processing error
   */
  static async logImportError(
    message: string,
    fileName?: string,
    rowNumber?: number,
    componentName?: string
  ): Promise<void> {
    await this.logError({
      errorType: 'import',
      errorMessage: message,
      componentName,
      actionName: 'file_import',
      severity: 'error',
      metadata: { fileName, rowNumber }
    });
  }

  /**
   * Log an API error
   */
  static async logApiError(
    message: string,
    endpoint: string,
    statusCode?: number,
    responseData?: any,
    componentName?: string
  ): Promise<void> {
    await this.logError({
      errorType: 'api',
      errorMessage: message,
      componentName,
      actionName: 'api_call',
      severity: statusCode && statusCode >= 500 ? 'critical' : 'error',
      metadata: { endpoint, statusCode, responseData }
    });
  }

  /**
   * Log a database error
   */
  static async logDatabaseError(
    message: string,
    tableName?: string,
    operation?: string,
    componentName?: string
  ): Promise<void> {
    await this.logError({
      errorType: 'database',
      errorMessage: message,
      componentName,
      actionName: operation,
      severity: 'critical',
      metadata: { tableName, operation }
    });
  }

  // ==================== ADMIN FUNCTIONS ====================

  /**
   * Get error statistics (admin only)
   */
  static async getErrorStats(daysBack: number = 7): Promise<ErrorStats | null> {
    try {
      const { data, error } = await supabase
        .rpc('get_error_stats', { days_back: daysBack })
        .single();

      if (error) throw error;
      const d = data as any;
      return {
        total_errors: d.total_errors || 0,
        critical_errors: d.critical_errors || 0,
        unresolved_errors: d.unresolved_errors || 0,
        errors_by_type: d.errors_by_type || {},
        errors_by_page: d.errors_by_page || {},
        top_affected_users: d.top_affected_users || []
      };
    } catch (error) {
      console.error('Error fetching error stats:', error);
      return null;
    }
  }

  /**
   * Get recent errors (admin only)
   */
  static async getRecentErrors(
    limit: number = 50,
    includeResolved: boolean = false
  ): Promise<ErrorLog[]> {
    try {
      const { data, error } = await supabase
        .rpc('get_recent_errors', {
          limit_count: limit,
          include_resolved: includeResolved
        });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching recent errors:', error);
      return [];
    }
  }

  /**
   * Mark error as resolved (admin only)
   */
  static async resolveError(
    errorId: string,
    adminUserId: string,
    notes?: string
  ): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .rpc('resolve_error', {
          error_id: errorId,
          admin_user_id: adminUserId,
          notes: notes || null
        });

      if (error) throw error;
      return data || false;
    } catch (error) {
      console.error('Error resolving error:', error);
      return false;
    }
  }

  // ==================== UTILITIES ====================

  /**
   * Setup global error handlers
   */
  private static setupGlobalErrorHandlers(): void {
    // Global error handler
    window.addEventListener('error', (event) => {
      this.logError({
        errorType: 'runtime',
        errorMessage: event.message,
        errorStack: event.error?.stack,
        severity: 'error',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno
        }
      });
    });

    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.logError({
        errorType: 'runtime',
        errorMessage: event.reason?.message || String(event.reason),
        errorStack: event.reason?.stack,
        severity: 'error',
        metadata: {
          type: 'unhandled_rejection'
        }
      });
    });
  }

  /**
   * Get browser information
   */
  private static getBrowserInfo(): Record<string, any> {
    return {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight
    };
  }

  /**
   * Log info message (non-error tracking)
   */
  static async logInfo(message: string, metadata?: Record<string, any>): Promise<void> {
    await this.logError({
      errorType: 'unknown',
      errorMessage: message,
      severity: 'info',
      metadata
    });
  }
}
